import { useContext } from 'react'
import {
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json'

/**
 * ReactiveJSON primitive — renders an amount as a localized currency string.
 *
 * Uses Intl.NumberFormat with { style: 'currency' }. The currency code comes
 * from the data (ISO 4217, e.g. EUR/USD/GBP), so every place that shows an
 * amount respects the record's own currency instead of a hardcoded symbol.
 *
 *   - type: FormatCurrency
 *     value: ~~.deal.data.amount        # number or numeric string
 *     currency: ~~.deal.data.currency   # ISO 4217 code (default 'EUR')
 *     decimals: 4                        # optional, overrides the currency's
 *                                        # default precision: a per-call API
 *                                        # cost is a fraction of a cent, and
 *                                        # the usual 2 decimals would render
 *                                        # every row as zero
 *     locale: fr-FR                      # optional, default 'fr-FR'
 *     empty: '—'                         # optional fallback when value empty/invalid
 *     compactFrom: 10000                 # optional: from this absolute amount on,
 *                                        # compact notation with ONE decimal
 *                                        # (fr-FR: 11 200 → "11,2 k €",
 *                                        # 1 250 000 → "1,3 M €"); the exact
 *                                        # amount stays in a tooltip
 *
 * Unknown/invalid currency codes fall back to a plain number + the raw code,
 * so a bad value never throws.
 */
export const FormatCurrency = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const raw = resolve(props.value)
  const currency = String(resolve(props.currency) || 'EUR').toUpperCase()
  const locale = resolve(props.locale) || 'fr-FR'
  const empty = resolve(props.empty) ?? ''
  const decimals = Number(resolve(props.decimals))
  const precision = Number.isFinite(decimals)
    ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
    : {}
  const compactFrom = Number(resolve(props.compactFrom))

  if (raw == null || raw === '') return <>{empty}</>
  const n = Number(raw)
  if (Number.isNaN(n)) return <>{empty}</>

  const format = (options) => {
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency, ...options }).format(n)
    }
    catch {
      // Unknown/invalid ISO code → graceful fallback (number + raw code).
      return `${new Intl.NumberFormat(locale, options).format(n)} ${currency}`
    }
  }

  // Large amounts: compact with one decimal. The exact amount goes to the mouse
  // (`title`) AND to screen readers (sr-only text, the abbreviation hidden from
  // them): a `title` on a generic span is not reliably announced.
  if (compactFrom > 0 && Math.abs(n) >= compactFrom) {
    const exact = format(precision)
    return (
      <span title={exact}>
        <span aria-hidden="true">{format({ notation: 'compact', minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span>
        <span className="sr-only">{exact}</span>
      </span>
    )
  }
  return <>{format(precision)}</>
}
